// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { until } from 'lit/directives/until.js';
import apiFetch from '@wordpress/api-fetch';

// Import utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, sprintf } from '@wordpress/i18n';

// Add the nonce as middleware. This automatically adds the `X-WP-Nonce` header to requests.
apiFetch.use( apiFetch.createNonceMiddleware( UncannyAutomatorBackend.rest.nonce ) );

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uo-user-card' )
export class AutomatorUserCard extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: Number, attribute: 'user-id' } ) accessor userID;

	// eslint-disable-next-line require-jsdoc
	render() {
		// Check if the required ID exists
		if ( isEmpty( this.userID ) ) {
			return escapeHTML( __( "Error: User ID can't be empty", 'uncanny-automator' ) );
		}

		return html`${ until(
			apiFetch( {
				url: `${ UncannyAutomatorBackend.rest.base }automator/v1/user/${ this.userID }`
			} )
				.then( ( response ) => {
					return this._templateUser( {
						avatar: response.data.avatar,
						name: response.data.display_name,
						email: response.data.email
					} );
				} ).catch( ( error ) => {
					// Check if the user doesn't exist
					if ( error.data.error_code === 404 ) {
						return this._templateUserNotFound;
					} else {
						return escapeHTML( __( 'Error: The user data could not be retrieved because of an unknown problem', 'uncanny-automator' ) );
					}
				} ),
			this._templateLoading
		) }`;
	}

	/**
	 * Returns the template that displays the user data
	 *
	 * @param {Object} data The user data
	 * @param {String} data.avatar The URL of the user avatar
	 * @param {String} data.name The name of the user
	 * @param {String} data.email The email of the user
	 * @return {TemplateResult} The template with the user data
	 */
	_templateUser( { avatar, name, email } ) {
		return html`
			<div class="user">
				<img class="user__avatar" src="${ avatar }">
				<div class="user__name">${ name }</div>
				<div class="user__email">${ email }</div>
			</div>
		`;
	}

	/**
	 * Returns the template displayed while we're making the call to retrieve the user data
	 *
	 * @return {TemplateResult} The loading placeholder
	 */
	get _templateLoading() {
		return html`
			<div class="user user--placeholder">
				<uo-placeholder class="user__avatar-placeholder" width="32px" height="32px"></uo-placeholder>
				<div class="user__name-placeholder">
					<uo-placeholder width="50px"></uo-placeholder> <uo-placeholder width="70px"></uo-placeholder>
				</div>
				<uo-placeholder class="user__email-placeholder"></uo-placeholder>
			</div>
		`;
	}

	/**
	 * Returns the template displayed while the user data doesn't exist
	 *
	 * @return {TemplateResult} The template with the user not found
	 */
	get _templateUserNotFound() {
		return html`
			<div class="user">
				<div class="user__icon">
					<uo-icon id="user-xmark" size="16"></uo-icon>
				</div>
				<div class="user__name">${ escapeHTML( __( 'User not found', 'uncanny-automator' ) ) }</div>
				<div class="user__email">${ sprintf(
					/* translators: 1. The user ID */
					escapeHTML( __( 'User ID #%1$s', 'uncanny-automator' ) ),
					this.userID
				) }</div>
			</div>
		`;
	}
}
